/**
 * The founder's market assumptions, with a live local preview.
 *
 * THE PREVIEW IS NOT AUTHORITATIVE and says so. It runs the same formulas the
 * server runs — same file's worth of arithmetic, pinned to the same parity
 * fixtures — so that pressing Save cannot change a number the founder is looking
 * at. Every published figure is still the server's.
 *
 * WHAT SAVING DOES, stated before the button rather than after: it creates a new
 * immutable version and marks the existing analysis as needing a refresh. It
 * does NOT regenerate, and it does not spend one of the monthly runs. A save
 * that silently re-ran the analysis would spend a founder's credits by
 * correcting a typo.
 *
 * The form is built from `AnalysisFormPrimitives`, which is where the input
 * chrome lives — these fields used to fill with the same colour as the page
 * behind them and carried no border, so they were invisible in BOTH themes.
 * Grouping is by concern rather than by field order: what you are counting,
 * the three ranges the arithmetic needs, and the optional figures that only
 * ever appear as a comparison.
 */

import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  FormFooter,
  FormHeader,
  FormSection,
  RangeField,
  TextField,
} from './AnalysisFormPrimitives';
import {
  calculateBottomUp,
  describeRangeProblem,
  formatRange,
  parseAmount,
  type Range,
} from '@/lib/strategic-analysis/market-calculations';
import {
  type MarketAssumptions,
  parseMarketAssumptions,
} from '@/lib/strategic-analysis/market-assumptions';

type RangeKey =
  | 'serviceableCustomerUnits'
  | 'annualValuePerCustomer'
  | 'reachableShare'
  | 'totalCustomerUnits'
  | 'founderTam'
  | 'founderSam'
  | 'founderSom';

type RangeDraft = { low: string; base: string; high: string };

const REQUIRED_RANGES: { key: RangeKey; label: string; hint: string; unit?: string }[] = [
  {
    key: 'serviceableCustomerUnits',
    label: 'Serviceable customers',
    hint: 'How many customers you could realistically serve in your supported markets.',
    unit: 'customers',
  },
  {
    key: 'annualValuePerCustomer',
    label: 'Annual value per customer',
    hint: 'Annual contract value or annual spend for one customer.',
  },
  {
    key: 'reachableShare',
    label: 'Reachable share',
    hint: 'The share of the serviceable market you could realistically win, as a decimal — 0.03 is 3%.',
    unit: '0–1',
  },
];

const OPTIONAL_RANGES: { key: RangeKey; label: string; hint: string }[] = [
  {
    key: 'totalCustomerUnits',
    label: 'All potential customers',
    hint: 'Needed for TAM. Left empty, TAM is not computed rather than assumed.',
  },
  { key: 'founderTam', label: 'Your TAM', hint: 'Your own figure, for comparison.' },
  { key: 'founderSam', label: 'Your SAM', hint: 'Your own figure, for comparison.' },
  { key: 'founderSom', label: 'Your SOM', hint: 'Your own figure, for comparison.' },
];

const emptyDraft = (): RangeDraft => ({ low: '', base: '', high: '' });

function toRange(draft: RangeDraft): Range | null {
  const low = parseAmount(draft.low);
  const base = parseAmount(draft.base);
  const high = parseAmount(draft.high);
  if (low === null || base === null || high === null) return null;
  return { low, base, high };
}

function fromRange(range: { low: number; base: number; high: number } | undefined): RangeDraft {
  if (!range) return emptyDraft();
  return { low: String(range.low), base: String(range.base), high: String(range.high) };
}

interface MarketAssumptionsEditorProps {
  assumptions: MarketAssumptions | null;
  canEdit: boolean;
  isSaving?: boolean;
  onSave?: (assumptions: MarketAssumptions) => void;
}

export function MarketAssumptionsEditor({
  assumptions,
  canEdit,
  isSaving = false,
  onSave,
}: MarketAssumptionsEditorProps) {
  const colors = useThemeColors();

  const [currency, setCurrency] = useState(assumptions?.currency ?? 'CHF');
  const [baseYear, setBaseYear] = useState(String(assumptions?.baseYear ?? new Date().getFullYear()));
  const [customerUnit, setCustomerUnit] = useState(assumptions?.customerUnit ?? '');
  // THE DEFINITION FIELDS. Market Validation compares the founder's market to
  // the one a published base figure measures, field by field — without these
  // the comparison had nothing to compare and fell back to "the model wrote no
  // notes", which is a fact about the model rather than about the market.
  const [marketDefinition, setMarketDefinition] = useState(assumptions?.marketDefinition ?? '');
  const [geography, setGeography] = useState(assumptions?.geography ?? '');
  const [ranges, setRanges] = useState<Record<RangeKey, RangeDraft>>(() => ({
    serviceableCustomerUnits: fromRange(assumptions?.serviceableCustomerUnits),
    annualValuePerCustomer: fromRange(assumptions?.annualValuePerCustomer),
    reachableShare: fromRange(assumptions?.reachableShare),
    totalCustomerUnits: fromRange(assumptions?.totalCustomerUnits),
    founderTam: fromRange(assumptions?.founderTam),
    founderSam: fromRange(assumptions?.founderSam),
    founderSom: fromRange(assumptions?.founderSom),
  }));

  const setRange = (key: RangeKey, bound: keyof RangeDraft, value: string) => {
    setRanges((current) => ({ ...current, [key]: { ...current[key], [bound]: value } }));
  };

  /**
   * The preview. Runs only when every required range parses AND passes the same
   * validation the server applies — a half-typed entry shows the reason rather
   * than a figure computed from a partial number.
   */
  const preview = useMemo(() => {
    const serviceable = toRange(ranges.serviceableCustomerUnits);
    const value = toRange(ranges.annualValuePerCustomer);
    const share = toRange(ranges.reachableShare);
    if (!serviceable || !value || !share) {
      return { result: null, problem: null as string | null };
    }
    const problem = describeRangeProblem('serviceable customers', serviceable) ??
      describeRangeProblem('annual value', value) ??
      describeRangeProblem('reachable share', share);
    if (problem) return { result: null, problem };
    try {
      return {
        result: calculateBottomUp({
          serviceableCustomerUnits: serviceable,
          annualValuePerCustomer: value,
          reachableShare: share,
          totalCustomerUnits: toRange(ranges.totalCustomerUnits) ?? undefined,
        }),
        problem: null,
      };
    } catch (error) {
      return { result: null, problem: error instanceof Error ? error.message : 'Check these numbers.' };
    }
  }, [ranges]);

  /**
   * The exact payload this form would submit, run through the CANONICAL
   * contract — the same function the edge function validates with.
   *
   * Built here rather than inside the press handler so the button cannot offer
   * to send something the server will refuse. That is not a hypothetical: this
   * form used to send `serviceableCustomerUnits` and `totalCustomerUnits` to a
   * schema that required `customerUnits` and `founderSourceUrls` and recognised
   * neither, so every save came back "Invalid request body" and the founder had
   * no way to tell which field was wrong.
   */
  const submission = useMemo(() => {
    const draft: Record<string, unknown> = {
      currency: currency.trim().toUpperCase(),
      baseYear: /^\d{4}$/.test(baseYear.trim()) ? Number(baseYear.trim()) : baseYear.trim(),
      customerUnit: customerUnit.trim(),
      marketDefinition: marketDefinition.trim() || undefined,
      geography: geography.trim() || undefined,
      serviceableCustomerUnits: toRange(ranges.serviceableCustomerUnits) ?? undefined,
      annualValuePerCustomer: toRange(ranges.annualValuePerCustomer) ?? undefined,
      reachableShare: toRange(ranges.reachableShare) ?? undefined,
      totalCustomerUnits: toRange(ranges.totalCustomerUnits) ?? undefined,
      founderTam: toRange(ranges.founderTam) ?? undefined,
      founderSam: toRange(ranges.founderSam) ?? undefined,
      founderSom: toRange(ranges.founderSom) ?? undefined,
      // The founder's own estimates about their own company. Optional by
      // nature, and normalized to an array by the contract so nothing
      // downstream has to tell absent from empty.
      founderSourceUrls: assumptions?.founderSourceUrls ?? [],
      // Totals are deliberately NOT sent. The server computes every derived
      // figure; a client-supplied total would be a number nobody checked.
    };
    // `undefined` keys would be dropped by JSON.stringify anyway; removing them
    // here means the object handed to the contract is the object that travels.
    for (const key of Object.keys(draft)) {
      if (draft[key] === undefined) delete draft[key];
    }
    return { draft, parsed: parseMarketAssumptions(draft) };
  }, [currency, baseYear, customerUnit, marketDefinition, geography, ranges, assumptions]);

  const missing: string[] = [];
  if (customerUnit.trim().length === 0) missing.push('a customer unit');
  if (!/^\d{4}$/.test(baseYear.trim())) missing.push('a four-digit base year');
  if (!preview.result && !preview.problem) missing.push('the three required ranges');

  const canSave = canEdit && !isSaving && preview.result !== null && missing.length === 0 &&
    submission.parsed.ok;

  // Why the button is inert, in words. A grey button and eleven fields is a
  // dead end a founder cannot debug.
  //
  // A range PROBLEM is deliberately not repeated here — the preview sits
  // directly above this footer and already states it, in red, next to the
  // figures it invalidates. Saying it twice reads as two separate faults.
  //
  // A contract failure that is NOT one of the named gaps above is named
  // directly. It should be unreachable, and if it ever is reached the founder
  // gets the field rather than a button that silently does nothing.
  const blockedReason = missing.length > 0
    ? `Still needed: ${missing.join(', ')}.`
    : submission.parsed.ok
    ? null
    : `These figures cannot be saved yet: ${submission.parsed.issues[0]}`;

  const handleSave = () => {
    if (!onSave || !submission.parsed.ok) return;
    onSave(submission.parsed.value);
  };

  return (
    <View className="gap-4">
      <FormHeader
        title="Market assumptions"
        description="Your own figures. They are kept separate from researched evidence and are never used to support the independent estimate."
      />

      <FormSection
        title="What you are counting"
        description="These fix the unit and the frame every figure below is expressed in."
      >
        <TextField
          label="Customer unit"
          hint="What counts as one customer — a company, a site, a seat, a device?"
          value={customerUnit}
          onChangeText={setCustomerUnit}
          editable={canEdit}
          placeholder="e.g. a manufacturing site"
        />
        {/* The two fields the definition comparison needs. A published market
            figure measures SOME market in SOME place, and the analysis can only
            say whether it is comparable to yours if you have said what yours
            is. Left empty, the corresponding row is reported as unknown rather
            than assumed to match. */}
        <TextField
          label="Market definition"
          hint="The market you mean, in your own words. Compared against what a published figure measures."
          value={marketDefinition}
          onChangeText={setMarketDefinition}
          editable={canEdit}
          placeholder="e.g. predictive maintenance software for discrete manufacturing"
        />
        <TextField
          label="Geography"
          hint="Where these customers are. Compared against the geography of any published figure."
          value={geography}
          onChangeText={setGeography}
          editable={canEdit}
          placeholder="e.g. DACH"
        />
        <View className="flex-row gap-3">
          <View className="flex-1">
            <TextField
              label="Currency"
              hint="ISO code"
              value={currency}
              onChangeText={setCurrency}
              editable={canEdit}
              autoCapitalize="characters"
              placeholder="CHF"
            />
          </View>
          <View className="flex-1">
            <TextField
              label="Base year"
              hint="Four digits"
              value={baseYear}
              onChangeText={setBaseYear}
              editable={canEdit}
              keyboardType="number-pad"
              invalid={baseYear.trim().length > 0 && !/^\d{4}$/.test(baseYear.trim())}
              placeholder="2026"
            />
          </View>
        </View>
      </FormSection>

      <FormSection
        title="Your ranges"
        description="Low, base and high for each. The arithmetic needs all three of these; the preview below runs the moment they parse."
      >
        {REQUIRED_RANGES.map((field) => (
          <RangeField
            key={field.key}
            label={field.label}
            hint={field.hint}
            unit={field.unit}
            values={ranges[field.key]}
            onChange={(bound, text) => setRange(field.key, bound, text)}
            editable={canEdit}
          />
        ))}
      </FormSection>

      <Preview preview={preview} currency={currency} colors={colors} />

      <FormSection
        title="For comparison"
        description="Your own headline figures, shown beside the independent estimate. Left empty they are simply not compared — never assumed."
        tone="muted"
      >
        {OPTIONAL_RANGES.map((field) => (
          <RangeField
            key={field.key}
            label={field.label}
            hint={field.hint}
            values={ranges[field.key]}
            onChange={(bound, text) => setRange(field.key, bound, text)}
            editable={canEdit}
            optional
          />
        ))}
      </FormSection>

      {canEdit && onSave && (
        <FormFooter
          consequence="Saving creates a new assumptions version and marks the current market analysis as needing a refresh. It does not re-run the analysis and does not use one of your monthly runs."
          label="Save market assumptions"
          savingLabel="Saving…"
          canSave={canSave}
          isSaving={isSaving}
          blockedReason={blockedReason}
          onSave={handleSave}
          accessibilityLabel="Save market assumptions"
        />
      )}
    </View>
  );
}

/**
 * The payoff, so it reads as a result rather than as another form row.
 *
 * It is NOT authoritative and says so at the bottom — it runs the same formulas
 * the server runs, pinned to the same parity fixtures, so that pressing Save
 * cannot change a number the founder is looking at.
 */
function Preview({
  preview,
  currency,
  colors,
}: {
  preview: { result: ReturnType<typeof calculateBottomUp> | null; problem: string | null };
  currency: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="rounded-3xl p-4 gap-3"
      style={{
        backgroundColor: colors.primary[50],
        borderWidth: 1,
        borderColor: colors.primary[100],
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-center gap-2">
        <Text
          numberOfLines={1}
          className="flex-1 text-[11px] font-bold uppercase"
          style={{ color: colors.primary[700], letterSpacing: 0.6 }}
        >
          Preview
        </Text>
        <Text numberOfLines={1} className="text-[11px] shrink-0" style={{ color: colors.primary[700] }}>
          computed here
        </Text>
      </View>

      {preview.problem && (
        <Text className="text-[13px] leading-5" style={{ color: colors.error }}>
          {preview.problem}
        </Text>
      )}

      {!preview.result && !preview.problem && (
        <Text className="text-[13px] leading-5" style={{ color: colors.text.secondary }}>
          Fill in serviceable customers, annual value and reachable share to see a preview.
        </Text>
      )}

      {preview.result && (
        <>
          {preview.result.tam
            ? <PreviewRow label="TAM" value={formatRange(preview.result.tam, currency)} colors={colors} />
            : (
              <Text className="text-[12px] leading-4" style={{ color: colors.text.secondary }}>
                TAM not computed — add all potential customers below.
              </Text>
            )}
          <PreviewRow label="SAM" value={formatRange(preview.result.sam, currency)} colors={colors} />
          <PreviewRow label="SOM" value={formatRange(preview.result.som, currency)} colors={colors} />
          <Text className="text-[11px] leading-4" style={{ color: colors.text.tertiary }}>
            The same formulas the analysis uses. Published figures are always recomputed on the
            server.
          </Text>
        </>
      )}
    </View>
  );
}

function PreviewRow({
  label,
  value,
  colors,
}: {
  label: string;
  value: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="flex-row items-baseline gap-3">
      <Text
        numberOfLines={1}
        className="text-[11px] font-bold uppercase shrink-0"
        style={{ color: colors.primary[700], letterSpacing: 0.6, width: 38 }}
      >
        {label}
      </Text>
      <Text
        numberOfLines={2}
        className="flex-1 text-[15px] font-semibold text-right"
        style={{ color: colors.text.primary }}
      >
        {value}
      </Text>
    </View>
  );
}
